/**
 * Copyright 2022 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { NotificationManager } from 'react-notifications';
import { postData, resetNewForm } from '../../../actions/createNew';
import { serializeFormInput } from '../../../commonComponents/form2/utils/serializeFormInput';
import { ROUTES } from '../../../constants/routes';
import { ErrorAlert, FormHeader } from '../../../commonComponents/form2';
import { APIContext } from '../../../commonComponents/auth/APIContext';
import { createMachine } from '../../../graphql/createMachine';
import { Machine, MachineInput } from '../../../graphql/generated-types';
import NewAWSMachineForm from '../components/NewAWSMachineForm/MachineForm';

type NewAWSMachineProps = {
  // should be changed to `typeof store.dispatch` once store is properly typed
  dispatch: Dispatch<unknown>;
  errorInfo: Record<string, string> | null;
  errorMessages: string[] | null;
  isSending: boolean;
};

function NewAWSMachine({ dispatch, errorMessages, errorInfo, isSending }: NewAWSMachineProps) {
  const { requester } = React.useContext(APIContext);

  React.useEffect(() => {
    resetNewForm(dispatch);
  }, [dispatch]);

  const onSubmit = (formData: Partial<MachineInput>) => {
    const input = serializeFormInput({
      ...formData,
      customer_id: localStorage.getItem('currentCustomer'),
    });

    const callback = (data: { id: Machine['id'] }) => {
      if (data) {
        NotificationManager.success('Machine has been successfully created');
        browserHistory.push(`${ROUTES.machines.machines}?refresh=true`);
      } else {
        NotificationManager.error('Unable to create machine');
      }
    };

    postData(dispatch, createMachine, { input }, requester, callback);
  };

  return (
    <div className="main wide">
      <FormHeader
        title="New AWS machine"
        subtitle="Add a new AWS machine"
        backRoute={ROUTES.machines.machines}
      />
      {errorMessages && Array.isArray(errorMessages) && <ErrorAlert errors={errorMessages} />}
      <NewAWSMachineForm onSubmit={onSubmit} isSubmitting={isSending} errorInfo={errorInfo} />
    </div>
  );
}

type CreateNewMachineStoreSlice = {
  error: string[] | null;
  errorInfo: Record<string, string> | null;
  isSending: boolean;
};

const mapStateToProps = (state: { createNew: CreateNewMachineStoreSlice }) => {
  const { createNew } = state;

  return {
    errorMessages: createNew.error,
    errorInfo: createNew.errorInfo,
    isSending: createNew.isSending,
  };
};

export default connect(mapStateToProps)(NewAWSMachine);
